import { shouldRetryRequest } from '@luminovo/data-stack';
import { Query, QueryCache, QueryClient, QueryKey } from '@tanstack/react-query';

interface Meta {
    /** False: no UI side effect for the user on error; it is still logged to Sentry and the console. */
    globalErrorHandler?: boolean;
}

declare module '@tanstack/react-query' {
    interface Register {
        queryMeta: Meta;
        mutationMeta: Meta;
    }
}

export type QueryErrorListener = (error: unknown, query: Query<unknown, unknown, unknown, QueryKey>) => void;

/** Where each client's cache sends its errors, filled in by {@link setQueryErrorListener}. */
const queryErrorListeners = new WeakMap<QueryClient, { current: QueryErrorListener | undefined }>();

// No component here, so editing `GlobalQueryErrorHandler` hot-reloads it alone; editing this re-runs `App.tsx`.
/** Reads the listener per error, as the handler renders later, inside the router; errors before then go unreported. */
export function createAppQueryClient(): QueryClient {
    const listener: { current: QueryErrorListener | undefined } = { current: undefined };
    const client = new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 500, // Prevents a repeated network request if successful call made this number of milliseconds ago
                refetchOnWindowFocus: false,
                retry: shouldRetryRequest,
            },
        },
        queryCache: new QueryCache({
            onError: (error, query) => listener.current?.(error, query),
        }),
    });
    queryErrorListeners.set(client, listener);
    return client;
}

/** Sets where a client from {@link createAppQueryClient} sends its cache's errors. */
export function setQueryErrorListener(client: QueryClient, listener: QueryErrorListener): void {
    const slot = queryErrorListeners.get(client);
    if (!slot) {
        throw new Error('GlobalQueryErrorHandler needs a QueryClient made by createAppQueryClient');
    }
    slot.current = listener;
}
